import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export interface ProjectMatch {
  molecule_id: number;
  file_id: number;
  filename: string;
  project_id: number | null;
  project_name: string | null;
  mol_index: number | null;
}

interface FileGroup {
  file_id: number;
  filename: string;
  project_id: number | null;
  project_name: string | null;
  first_molecule_id: number;
  count: number;
}

interface ProjectMatchBannerProps {
  matches: ProjectMatch[];
  isLoading: boolean;
}

const MAX_VISIBLE = 3;

const ProjectMatchBanner: React.FC<ProjectMatchBannerProps> = ({ matches, isLoading }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const groups = useMemo<FileGroup[]>(() => {
    const map: Record<number, FileGroup> = {};
    for (const m of matches) {
      if (!map[m.file_id]) {
        map[m.file_id] = {
          file_id: m.file_id,
          filename: m.filename,
          project_id: m.project_id,
          project_name: m.project_name,
          first_molecule_id: m.molecule_id,
          count: 0,
        };
      }
      map[m.file_id].count++;
    }
    return Object.values(map);
  }, [matches]);

  if (!isLoading && groups.length === 0) return null;

  const handleClick = (g: FileGroup) => {
    navigate(`/chemistry/file/${g.file_id}/editor`, {
      state: { moleculeId: g.first_molecule_id },
    });
  };

  // Single file, single match — compact one-liner
  const isSingleMatch = groups.length === 1 && groups[0].count === 1;

  const visibleGroups = expanded ? groups : groups.slice(0, MAX_VISIBLE);
  const hiddenCount = groups.length - MAX_VISIBLE;

  return (
    <div
      style={{
        background: 'rgba(13, 148, 136, 0.08)',
        borderLeft: '3px solid #0D9488',
        borderRadius: 8,
        padding: isSingleMatch ? '10px 16px' : 16,
        marginBottom: 16,
        maxHeight: 180,
        overflowY: 'auto',
      }}
    >
      {/* Loading skeleton */}
      {isLoading && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <FolderIcon />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#0D9488' }}>Found in your projects</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1, 2].map((i) => (
              <div
                key={i}
                style={{
                  height: 20,
                  borderRadius: 4,
                  background: 'rgba(13, 148, 136, 0.12)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  width: i === 1 ? '60%' : '45%',
                }}
              />
            ))}
          </div>
          <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
        </>
      )}

      {/* Single match — compact layout */}
      {!isLoading && isSingleMatch && (
        <div
          onClick={() => handleClick(groups[0])}
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          <FolderIcon />
          <span style={{ fontSize: 13, color: '#0D9488' }}>
            <span style={{ fontWeight: 600 }}>Found in your projects:</span>
            {' '}
            {groups[0].project_name && <>{groups[0].project_name} &rsaquo; </>}
            {groups[0].filename}
          </span>
          <span style={{ fontSize: 12, color: '#0D9488', fontWeight: 500, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            Open &rarr;
          </span>
        </div>
      )}

      {/* Multiple matches — header + grouped rows */}
      {!isLoading && !isSingleMatch && groups.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <FolderIcon />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#0D9488' }}>Found in your projects</span>
          </div>

          {visibleGroups.map((g) => (
            <div
              key={g.file_id}
              onClick={() => handleClick(g)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '6px 8px',
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(13, 148, 136, 0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                {g.project_name && (
                  <>
                    <span style={{ fontWeight: 600, color: '#0D9488' }}>{g.project_name}</span>
                    <span style={{ color: 'var(--text-muted)', margin: '0 2px' }}>&rsaquo;</span>
                  </>
                )}
                <span style={{ color: '#0D9488' }}>{g.filename}</span>
                <span style={{ color: 'var(--text-muted)', marginLeft: 4, fontSize: 12 }}>
                  ({g.count} {g.count === 1 ? 'match' : 'matches'})
                </span>
              </div>
              <span style={{ fontSize: 12, color: '#0D9488', fontWeight: 500, whiteSpace: 'nowrap' }}>
                Open &rarr;
              </span>
            </div>
          ))}

          {hiddenCount > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: 12,
                cursor: 'pointer',
                padding: '4px 8px',
                marginTop: 2,
              }}
            >
              {expanded
                ? 'Show fewer \u25B4'
                : `and ${hiddenCount} more file${hiddenCount !== 1 ? 's' : ''}... Show all \u25BE`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

const FolderIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0D9488" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

export default ProjectMatchBanner;
